import React from 'react';
import { Button } from './Button';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    console.error('Error caught by boundary:', error);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      const containerStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 20px',
        textAlign: 'center',
        minHeight: '400px',
        backgroundColor: 'var(--color-light-grey)',
        borderRadius: '8px',
      };

      const headingStyle: React.CSSProperties = {
        fontSize: '20px',
        fontWeight: 600,
        fontFamily: 'var(--font-family)',
        color: 'var(--color-black)',
        margin: '0 0 12px 0',
      };

      const messageStyle: React.CSSProperties = {
        fontSize: '14px',
        fontFamily: 'var(--font-family)',
        color: 'var(--color-dark-grey)',
        margin: '0 0 24px 0',
        maxWidth: '500px',
        lineHeight: '1.5',
      };

      const errorDetailsStyle: React.CSSProperties = {
        backgroundColor: 'var(--color-white)',
        border: '1px solid #dc3545',
        borderRadius: '4px',
        padding: '12px',
        marginBottom: '20px',
        fontSize: '12px',
        fontFamily: 'monospace',
        color: '#dc3545',
        maxWidth: '500px',
        textAlign: 'left',
        wordBreak: 'break-word',
      };

      return (
        <div style={containerStyle}>
          <h1 style={headingStyle}>Something went wrong</h1>
          <p style={messageStyle}>
            An unexpected error occurred. Refresh the page or contact support if the problem continues.
          </p>
          {this.state.error && (
            <div style={errorDetailsStyle}>
              <strong>Details:</strong>
              <div>{this.state.error.message}</div>
            </div>
          )}
          <Button variant="primary" onClick={this.handleRetry}>
            Retry
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}
